import type { GetServerSideProps, NextPage } from 'next';
import Head from 'next/head';
import { NonGatedThemeProvider } from '@lemon-io/ui-kit';
import { dehydrate, QueryClient } from '@tanstack/react-query';

import { profileApi } from '@internal/api/profile';
import { projectsApi } from '@internal/api/projects';
import { extractToken } from '@internal/api/system/utils';
import { tagsApi } from '@internal/api/tags';
import AppRoute, { appPageProps } from '@internal/components/app/route';
import { ApiContextProvider } from '@internal/components/common/api-context';
import Page from '@internal/components/common/page';
import ProjectViewPage from '@internal/components/project-view';
import { Profile } from '@internal/types/profile';

const PAGE_TITLE = 'Project | Lemon.io';

export const getServerSideProps: GetServerSideProps = async context => {
  const { dealId } = context.query as { dealId: string };
  const token = extractToken(context);

  const queryClient = new QueryClient();

  const queries = [] as Array<Promise<void>>;

  let profile: Profile | null = null;

  if (token) {
    try {
      profile = await profileApi.getProfile.fetch(queryClient, null, context);
    } catch (_) {
      /* empty */
    }
  }

  if (profile) {
    queries.push(
      projectsApi.getProject.prefetch(
        queryClient,
        {
          dealId,
          role: profile.role,
        },
        context
      ),
      ...(profile.apiKey
        ? [
            profileApi.getSkills.prefetch(queryClient, profile.apiKey, context),
            tagsApi.getContactProjectTags.prefetch(
              queryClient,
              { contactKey: profile.apiKey },
              context
            ),
          ]
        : [])
    );
  } else {
    queries.push(
      projectsApi.getProject.prefetch(
        queryClient,
        {
          dealId,
        },
        context
      )
    );
  }

  queries.push(
    tagsApi.getHubspotProjectTags.prefetch(
      queryClient,
      { hubspotProjectId: dealId as string },
      context
    )
  );

  await Promise.all(queries);

  const project = queryClient.getQueryData(['project', dealId]);

  if (!project) {
    return {
      notFound: true,
    };
  }

  const dehydratedState = dehydrate(queryClient);
  const isPublicView = !profile;

  return {
    props: {
      dealId,
      dehydratedState,
      isPublicView,
      ...(isPublicView
        ? { withoutLayout: true, lightBackground: true, appTheme: true }
        : appPageProps),
    },
  };
};

interface ProjectsPageProps {
  dealId: string;
  isPublicView?: boolean;
}

const ProjectsPage: NextPage<ProjectsPageProps> = ({ dealId, isPublicView }) =>
  isPublicView ? (
    <Page>
      <Head>
        <title>{PAGE_TITLE}</title>
      </Head>
      <ApiContextProvider notLogginedAllowed>
        <NonGatedThemeProvider>
          <ProjectViewPage dealId={dealId} />
        </NonGatedThemeProvider>
      </ApiContextProvider>
    </Page>
  ) : (
    <AppRoute title={PAGE_TITLE}>
      <ProjectViewPage dealId={dealId} />
    </AppRoute>
  );

export default ProjectsPage;
